import DualAxes, { DualAxesConfig } from '@ant-design/plots/es/components/dual-axes';
import { Chart } from '@ant-design/plots/es/interface';
import { FC, RefObject, useRef } from 'react';

import { getDateFactory } from '@/common/utils';

import { SearchConsoleAnalyticsRowKeys, SearchConsoleAnalyticsRowKeysColor } from '@/modules/Domains';
import { SearchConsoleAnalytics, SearchConsoleAnalyticsDatesDimension, SearchConsoleAnalyticsDefault } from '@/modules/DomainsApi';

import { parseNumberValue } from '../helpers';
import './SearchConsoleAnalyticsChart.scss';

const AnalyticsPropertiesTitle: {
  long: Record<SearchConsoleAnalyticsRowKeys, string>;
  short: Record<SearchConsoleAnalyticsRowKeys, string>;
} = {
  long: {
    clicks: 'Усього кліків',
    impressions: 'Усього показів',
    ctr: 'Середній CTR',
    position: 'Середня позиція',
  },
  short: {
    clicks: 'Звертання',
    impressions: 'Покази',
    ctr: 'CTR',
    position: 'Позиція',
  }
};

interface Props {
  noRealData: boolean;
  defaultValues: SearchConsoleAnalyticsDefault;
  datesRows: SearchConsoleAnalytics['data']['dates'] | undefined;
  itemsColor: SearchConsoleAnalyticsRowKeysColor;
}

const searchConsoleAnalyticsRowKeys: SearchConsoleAnalyticsRowKeys[] = ['clicks', 'impressions', 'ctr', 'position'] as const;

const getPositionAxisMax = (rows: Props['datesRows']): number => {
  const max = Math.max(2, ...(rows ?? []).map(d => d.position ?? 0));
  const step = 10 ** Math.floor(Math.log10(max));
  return Math.ceil(max / step) * step;
};

const getIsolatedRows = (rows: SearchConsoleAnalyticsDatesDimension[], key: SearchConsoleAnalyticsRowKeys) =>
  rows.filter((d, i) => d[key] != null && rows[i - 1]?.[key] == null && rows[i + 1]?.[key] == null);

export const SearchConsoleAnalyticsChartView: FC<Props> = ({
  datesRows,
  defaultValues,
  itemsColor,
  noRealData
}) => {
  const chartRef = useRef<Chart | null>(null);
  const positionAxisMax = getPositionAxisMax(datesRows);

  const rows: SearchConsoleAnalyticsDatesDimension[] = (datesRows ?? []).map(d => ({
    ...d,
    position: d.position || null,
    ctr: d.ctr || null,
  }));

  const yScales = {
    clicks: { type: 'linear' as const },
    impressions: { type: 'linear' as const },
    ctr: {},
    position: {
      nice: false,
      range: [0, 1],
      domain: [1, positionAxisMax],
      tickMethod: () => [...new Set([1, Math.round(positionAxisMax / 2), positionAxisMax])],
    },
  };
  // Line and its dots share one y scale (and axis) by `key`; G2 ignores `key` for independent scales
  const yScale = (key: SearchConsoleAnalyticsRowKeys) => ({ y: { ...yScales[key], key, independent: false } });

  const config: DualAxesConfig = {
    onReady: (plot) => {
      chartRef.current = plot;
    },
    className: 'chart-canvas',
    data: rows,
    xField: 'key',
    axis: {
      x: {
        labelFormatter: (d: string) => getDateFactory(new Date(d), '08/16/2018'),
      },
    },
    legend: {
      size: 20,
      color: {
        // itemSpacing: 20,
        // itemSpan: 6,
        // itemBackgroundFill: 'red',
        // labelSpacing: 10,
        // colPadding: 0,
        rowPadding: 10,
        itemMarkerSize: 12,
        itemLabelText: (_: unknown, index: number) =>
          Object.values(AnalyticsPropertiesTitle.long)[index]
          + '\t\t–\t\t'
          + parseNumberValue(defaultValues[searchConsoleAnalyticsRowKeys[index]], index === 2)
      }
    },
    shapeField: 'smooth',
    scale: {
      y: { nice: true, tickCount: 2 },
      color: { range: [itemsColor.clicks, itemsColor.impressions, itemsColor.ctr, itemsColor.position] }
    },
    height: 400,
    style: { lineWidth: 2 },
    interaction: {
      legendFilter: !noRealData,
      tooltip: {
        sort: (d: { name: string } & unknown) => Object.keys(AnalyticsPropertiesTitle.short).indexOf(d.name),
        render: tooltipRender,
      }
    },
    children: [
      {
        type: 'line',
        yField: 'clicks',
        scale: yScale('clicks'),
        axis: {
          y: {
            position: 'left',
            labelFormatter: (d: number) => yLabelRender({
              chartRef,
              yText: parseNumberValue(d, false, false),
              color: itemsColor.clicks,
              direction: 'left',
            })
          },
        },
        tooltip: {
          items: [{ channel: 'y', valueFormatter: parseNumberValue }],
        },
      },
      {
        type: 'line',
        yField: 'impressions',
        scale: yScale('impressions'),
        axis: { y: {
          position: 'right',
          labelFormatter: (d: number) => yLabelRender({
            chartRef,
            yText: parseNumberValue(d, false, false),
            color: itemsColor.impressions,
            direction: 'right',
          })
        }, },
        tooltip: {
          items: [{ channel: 'y', valueFormatter: parseNumberValue }],
        },
      },
      {
        type: 'line',
        yField: 'ctr',
        scale: yScale('ctr'),
        axis: { y: {
          position: 'right',
          labelFormatter: (d: number) => yLabelRender({
            chartRef,
            yText: parseNumberValue(d, true, false),
            color: itemsColor.ctr,
            direction: 'right',
          })
        }, },
        tooltip: {
          items: [{ channel: 'y', valueFormatter: (d: number) => parseNumberValue(d, true) }],
        },
      },
      {
        type: 'line',
        yField: 'position',
        scale: yScale('position'),
        axis: { y: {
          position: 'left',
          labelFormatter: (d: number) => yLabelRender({
            chartRef,
            yText: parseNumberValue(d, false, false),
            color: itemsColor.position,
            direction: 'left',
          })
        }, },
        tooltip: {
          items: [{
            channel: 'y',
            valueFormatter: (d: number) => parseNumberValue(d, false)
          }],
        },
      },
      // A line can't draw a lone value (1-day range or gaps on both sides), so it's drawn as a dot
      ...searchConsoleAnalyticsRowKeys.flatMap((key) => {
        const isolatedRows = getIsolatedRows(rows, key);
        return isolatedRows.length
          ? [{ type: 'point', yField: key, data: isolatedRows, scale: yScale(key), shapeField: 'point', tooltip: false }]
          : [];
      }),
    ],
  };

  // useEffect(() => {
  //   chartRef.current?.chart.on(
  //     'legend:filter',
  //     (e: { nativeEvent: unknown; data: { channel: string, values: string[] } }) => {
  //       const { nativeEvent, data: dt } = e;
  //       return setLegendItemsCount(dt.values.length);
  //       if (!nativeEvent) return;
  //       console.log(dt.values);
  //       // chartRef?.chart.emit('legend:filter', {
  //       //   dt: { channel: 'color', values: dt.values },
  //       // });

  //     });

  //   return () => {
  //     chartRef.current?.chart.off('legend:filter');
  //   };
  // }, []);

  return (
    <div>
      { /* <Space.Compact>
        <Button
          onClick={ () => chartRef.current?.chart?.emit('legend:filter', {
            data: { channel: 'color', values: keys.filter(el => el !== 'clicks') },
          }) }>
          Click me
        </Button>
        <Button
          onClick={ () => chartRef.current?.chart?.emit('legend:filter', {
            data: { channel: 'color', values: keys.filter(el => el !== 'ctr') },
          }) }>
          Click me
        </Button>
        <Button
          onClick={ () => chartRef.current?.chart?.emit('legend:filter', {
            data: { channel: 'color', values: keys.filter(el => el !== 'impressions') },
          }) }>
          Click me
        </Button>
        <Button
          onClick={ () => chartRef.current?.chart?.emit('legend:filter', {
            data: { channel: 'color', values: keys.filter(el => el !== 'position') },
          }) }>
          Click me
        </Button>
        <Button onClick={ () => chartRef.current?.chart?.emit('legend:reset', {}) }>
          reset
        </Button>
      </Space.Compact> */ }

      <DualAxes { ...config }/>
    </div>
  );
};

const tooltipRender = (
  _: unknown,
  { title, items }: { title: string; items: ({ color: string; name: keyof typeof AnalyticsPropertiesTitle.short; value: unknown; })[]}
) => `<div style="width: max-content;">
<div style="margin-bottom: 8px;">${ getDateFactory(new Date(title), 'Thursday, August 16, 2018') }</div>
<div style="display: flex; justify-content: space-between;">
<ul style="list-style-type: none; padding: 0; margin: 0; width: 100%;">
${ items.map((it, idx) => `
  <li style="${ items.length === idx + 1 ? '' : 'margin-bottom: 8px;' } display: flex; flex-direction: row; align-items: center; justify-content: space-between; gap: 18px;">
    <div style="display: flex; flex-direction: row; align-items: center; gap: 4px;"">
      <span style="border-radius: 2px; width: 10px; height: 3px; display: flex; background-color: ${ it.color };"></span>
      <span style="color: ${ it.color };">${ AnalyticsPropertiesTitle.short[it.name] }</span>
    </div>
    <span style="font-weight: bold;">${ it.value }</span>
  </li>
`).join('') }
</ul>`;

const yLabelRender = (params: {
  yText: number | string;
  chartRef: RefObject<Chart | null>;
  color: string;
  direction: 'left' | 'right';
}) => {
  const { yText, chartRef, color, direction } = params;

  const chart = chartRef.current?.chart;
  const doc = chart?.getContext()?.canvas?.document;
  const group = doc?.createElement('g', {});
  group?.append(doc.createElement('text', {
    style: {
      text: yText.toString(),
      fill: color,
      textAlign: direction === 'left' ? 'right' : 'left',
      fontSize: 12,
      display: 'flex',
      transform: 'translate(0, 8)',
    },
  }));
  return group;
};
